import { Component, OnInit } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { KnowHowMessageModalComponent } from '../know-how-message-modal/know-how-message-modal.component';
import { SportService } from '../../sports/services/sports.service';
import { SportTrainersModel } from '../../sports/models/sport-trainers.model';
import { SportTrainerRoleEnum } from '../../sports/enums/sport-trainer-role.enum';
import { SportTrainerContactModel } from '../../sports/models/sport-trainer-contact.model';
import { UserAuthorizationService } from '../../security/services/user-authorization.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-know-how-contact-info',
    templateUrl: './know-how-contact-info.component.html',
    styleUrl: './know-how-contact-info.component.scss',
})
export class KnowHowContactInfoComponent implements OnInit {

    sportTrainers: SportTrainersModel[] | null = null;

    sportTrainerRoleEnum = SportTrainerRoleEnum;

    constructor(
        private bsModalService: BsModalService,
        private sportService: SportService,
        private authService: UserAuthorizationService
    ) { }

    ngOnInit(): void {
        this.loadTrainerSportsTrainers();
    }

    protected sendMessageToSelected(sportTrainers: SportTrainersModel): void {
        let filteredTrainers = this.getFilteredTrainers(sportTrainers);

        let selectedTrainers = filteredTrainers.filter(x => x.isSelected);
        let selectedAdmins = sportTrainers.trainerAdmins.filter(x => x.isSelected);

        this.openMessageModal(sportTrainers.sportId, selectedAdmins, selectedTrainers);
    }

    protected sendMessageToAll(sportTrainers: SportTrainersModel): void {
        let filteredTrainers = this.getFilteredTrainers(sportTrainers);
        this.openMessageModal(sportTrainers.sportId, sportTrainers.trainerAdmins, filteredTrainers);
    }

    protected hasSelectedContacts(sportTrainers: SportTrainersModel): boolean {
        const filteredTrainers = this.getFilteredTrainers(sportTrainers);

        const trainers = filteredTrainers.some(x => x.isSelected);
        const admins = sportTrainers.trainerAdmins.some(x => x.isSelected);

        return trainers || admins;
    }

    protected selectionChanged(sportTrainers: SportTrainersModel) {
        const trainerSelected = this.hasAnyTrainerSelected(sportTrainers)
        if (!trainerSelected) return;
        this.autoSelectAdmins(sportTrainers);
    }

    protected hasAnyTrainerSelected(sportTrainer: SportTrainersModel): boolean {
        let filteredContacts = this.getFilteredTrainers(sportTrainer);
        return filteredContacts.some(trainer => trainer.isSelected);
    }

    protected isAnyFilterAcitve(sportTrainer: SportTrainersModel): boolean {
        if (sportTrainer.trainerContacts == null) return false;

        let filteredContacts = this.getFilteredTrainers(sportTrainer);
        if (filteredContacts == null) return false;

        return (sportTrainer.trainerContacts.length != filteredContacts.length);
    }

    protected toggleAttribute(sportTrainers: SportTrainersModel, attributeId: number): void {
        if (sportTrainers.selectedAttributeIds == null) {
            sportTrainers.selectedAttributeIds = [];
        }

        const index = sportTrainers.selectedAttributeIds.indexOf(attributeId);
        if (index === -1) {
            sportTrainers.selectedAttributeIds.push(attributeId);
        } else {
            sportTrainers.selectedAttributeIds.splice(index, 1);
        }

        this.selectionChanged(sportTrainers);
    }

    protected isAttributeSelected(sportTrainers: SportTrainersModel, attributeId: number): boolean {
        if (sportTrainers.selectedAttributeIds == null) return false;
        const index = sportTrainers.selectedAttributeIds.indexOf(attributeId);

        return (index !== -1);
    }

    protected getFilteredTrainers(sportTrainers: SportTrainersModel): SportTrainerContactModel[] {
        if (!sportTrainers.selectedAttributeIds?.length) return sportTrainers.trainerContacts;

        return sportTrainers.trainerContacts.filter(contact =>
            contact.attributes?.some(attr =>
                sportTrainers.selectedAttributeIds.includes(attr.id)
            )
        );
    }

    private autoSelectAdmins(sportTrainers: SportTrainersModel): void {
        sportTrainers.trainerAdmins.forEach((admin) => {
            admin.isSelected = true;
        });
    }

    private openMessageModal(sportId: number, trainerAdmins: SportTrainerContactModel[], trainerContacts: SportTrainerContactModel[]): void {
        this.bsModalService.show(
            KnowHowMessageModalComponent, {
            ignoreBackdropClick: true,
            initialState: {
                sportId: sportId,
                trainerAdmins: trainerAdmins,
                trainerContacts: trainerContacts
            }
        })
    }

    protected sendMessageToSelectedViaMailClient(sportTrainers: SportTrainersModel): void {
        const { admins, trainers } = this.getSelectedContacts(sportTrainers);
        this.openMailClient(admins, trainers);
    }

    protected sendMessageToAllViaMailClient(sportTrainers: SportTrainersModel): void {
        const trainers = this.getFilteredTrainers(sportTrainers);
        this.openMailClient(sportTrainers.trainerAdmins, trainers);
    }

    private getSelectedContacts(sportTrainers: SportTrainersModel): {
        admins: SportTrainerContactModel[];
        trainers: SportTrainerContactModel[];
    } {
        const filteredTrainers = this.getFilteredTrainers(sportTrainers);

        return {
            admins: sportTrainers.trainerAdmins.filter(a => a.isSelected),
            trainers: filteredTrainers.filter(t => t.isSelected)
        };
    }

    private openMailClient(admins: SportTrainerContactModel[], trainers: SportTrainerContactModel[]): void {
        
        const recipients = [...trainers]
            .map(x => x.email)
            .filter(Boolean)
            .join(';');

        const ccRecipients = [...admins, this.authService.user]
            .map(x => x?.email)
            .filter(Boolean)
            .join(';');

        const bccRecipients = environment.defaultEmailRecipients.join(';');

        const params = new URLSearchParams({
            cc: ccRecipients,
            bcc: bccRecipients,
        });

        window.location.href = `mailto:${recipients}?${params.toString()}`;
    }

    private loadTrainerSportsTrainers(): void {
        this.sportService.getSportTrainer().subscribe({
            next: (sportTrainers) => {
                if (sportTrainers) {
                    sportTrainers = sportTrainers.sort((a, b) => a.sportName.localeCompare(b.sportName));
                    sportTrainers.forEach((sportTrainer) => {
                        if (sportTrainer.trainerAdmins) {
                            sportTrainer.trainerAdmins = sportTrainer.trainerAdmins.sort((a, b) => (`${a.role}` + a.firstName + b.lastName).localeCompare((`${b.role}` + b.firstName + b.lastName)));
                        }

                        if (sportTrainer.trainerContacts) {
                            sportTrainer.trainerContacts = sportTrainer.trainerContacts.sort((a, b) => (a.firstName + b.lastName).localeCompare((b.firstName + b.lastName)));

                            sportTrainer.trainerContacts.forEach((contact) => {
                                contact.attributes = contact.attributes.sort((a, b) => a.name.localeCompare(b.name));
                            });
                        }

                        if (sportTrainer.attributes) {
                            sportTrainer.attributes = sportTrainer.attributes.sort((a, b) => a.name.localeCompare(b.name));
                        }
                    });
                }
                this.sportTrainers = sportTrainers;
            },
        });
    }
}
